import { memo, useEffect, useState } from 'react';
import { AppWindow, MonitorUp } from 'lucide-react';
import { Button, Modal } from '@cyphex/ui';
import { desktopBridge, hasEchoCancelledSystemAudio, isDesktopShell } from '@/features/desktop/bridge';
import type { ScreenShareHint } from '../media/contract';
import { ToggleRow } from './settings/SettingRow';

export interface ScreenSharePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `computerSound` is the "Include computer sound" switch. */
  onStart: (hint: ScreenShareHint, computerSound: boolean) => void;
}

/**
 * What "Include computer sound" means where this person is sharing from, said
 * when they turn it on: `hint` when it simply works, `warning` when it does not
 * or costs something.
 *
 * On the Windows desktop client the only sound there is to share is everything
 * the machine plays — this call included. Desktop 0.1.1 and later take the call
 * back out with echo cancellation (`hasEchoCancelledSystemAudio`); an older
 * install cannot, so the others hear their own voices come back, and the
 * presenter is told so and told how to end it. A browser is asked for a tab's
 * sound only (`localMedia.captureWithSound`), which cannot be this call.
 */
function soundNotice(
  desktop: boolean,
  platform: string | undefined,
  echoCancelled: boolean,
): { tone: 'hint' | 'warning'; text: string } {
  if (!desktop) {
    return {
      tone: 'hint',
      text: 'Choose the browser tab that is playing the video and keep "Share tab audio" ticked. ' +
        'The sound of a whole screen or a window is not shared: it would send this call back to everyone.',
    };
  }
  if (platform === 'win32' && echoCancelled) {
    return {
      tone: 'hint',
      text: 'Everything your computer plays is shared, such as a video, with this call’s own audio ' +
        'removed, so nobody hears an echo.',
    };
  }
  if (platform === 'win32') {
    return {
      tone: 'warning',
      text: 'This version of the desktop app shares everything your computer plays, including this ' +
        'call, so the others will hear their own voices echo back while it is on. Install the latest ' +
        'Cyphex Teams desktop app to share computer sound without the echo.',
    };
  }
  return {
    tone: 'warning',
    text: 'The desktop app cannot share your computer’s sound on this system. Share from the ' +
      'browser to include a tab’s sound.',
  };
}

/**
 * Cyphex picks the *encoding profile*; the SOURCE is picked in the next step.
 * `detail` = 15 fps crisp slides, `motion` = 30 fps video — the wrong hint costs
 * ~1.1 Mbps per subscriber (study §4.2).
 *
 * This dialog used to offer "Entire screen / Application window / Browser tab"
 * buttons. They were removed because nothing was wired to them: the choice was
 * discarded and whatever chooser came next decided the source anyway. A control
 * that does nothing is worse than no control, so the dialog now says plainly
 * where the choice actually happens — the desktop client's own source picker
 * (screens and windows with live thumbnails), or the browser's chooser.
 *
 * COMPUTER SOUND IS OFF EVERY TIME THE DIALOG OPENS. It used to be asked for on
 * every share, and on Windows that sent every other participant's voice back to
 * them — see `ShareStartOptions`. The room hears the presenter's microphone
 * either way; this switch is for a video's soundtrack, and it is turned on for
 * one share at a time, deliberately.
 */
export const ScreenSharePicker = memo(function ScreenSharePicker({
  open, onOpenChange, onStart,
}: ScreenSharePickerProps) {
  const [hint, setHint] = useState<ScreenShareHint>('detail');
  const [computerSound, setComputerSound] = useState(false);
  const desktop = isDesktopShell();
  const notice = soundNotice(desktop, desktopBridge()?.platform, hasEchoCancelledSystemAudio());

  useEffect(() => {
    if (open) setComputerSound(false);
  }, [open]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Share content"
      description="Choose how it should be encoded, then pick what to share."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" leftIcon={MonitorUp} onClick={() => onStart(hint, computerSound)}>
            Continue
          </Button>
        </>
      }
    >
      <fieldset>
        <legend className="mb-2 text-base font-semibold">Optimise for</legend>
        <div className="flex gap-2">
          <Button
            variant={hint === 'detail' ? 'subtle' : 'ghost'}
            size="sm"
            onClick={() => setHint('detail')}
            aria-pressed={hint === 'detail'}
          >
            Text and slides · 15 fps
          </Button>
          <Button
            variant={hint === 'motion' ? 'subtle' : 'ghost'}
            size="sm"
            onClick={() => setHint('motion')}
            aria-pressed={hint === 'motion'}
          >
            Video and motion · 30 fps
          </Button>
        </div>
      </fieldset>

      <div className="mt-3">
        <ToggleRow
          label="Include computer sound"
          hint={
            computerSound && notice.tone === 'hint'
              ? notice.text
              : 'Everyone hears your microphone either way. Turn this on only to play a video with its sound.'
          }
          notice={computerSound && notice.tone === 'warning' ? notice.text : null}
          checked={computerSound}
          onChange={setComputerSound}
        />
      </div>

      <p className="mt-3 flex items-start gap-2 rounded-lg bg-subtle p-3 text-base text-muted-foreground">
        <AppWindow size={16} className="mt-0.5 shrink-0" aria-hidden />
        <span>
          {desktop
            ? 'Next you will choose a screen or a single window from your machine, with a preview of each.'
            : 'Your browser will ask which screen, window or tab to share.'}
        </span>
      </p>
    </Modal>
  );
});
